import { useEffect, useRef, useState } from 'react'
import { Profile } from '@/shared/store/useAppStore'
import { debounce } from '@mui/material'
import { getErrorMessage } from '@/shared/lib'
import {
    debounceSpeedMedium,
    defaultProfile,
    MAX_PROFILES_LIMIT,
} from '@/shared/constants'
import { usePinVerification } from '@/widgets/ProfileScreen/hooks/usePinVerification'
import { useAuthenticationConfig } from '@/widgets/ProfileScreen/hooks/useAuthenticationConfig'
import { useProfileManagement } from '@/widgets/ProfileScreen/hooks/useProfileManagement'
import { useLoginAndProfileRepository } from '@/features/repositories/useLoginAndProfileRepository'
import useFavoritesChannels from '@/features/favorites/useFavoritesChannels'
import { useEPGCache } from '@/shared/cache/hooks/useEPGCache'
import { saveToLocalStorage } from '@/shared/localStorage/localStorageRepository'
import {
    useEPGStore,
    useFilterStore,
    useTVStore,
    useVODStore,
} from '@/shared/store'
import { useNavigate } from 'react-router-dom'

export interface INewProfile {
    version: number
    profile_name: string
    isKid: boolean
    order: string
    iconId: string
}

const blankProfile = {
    version: 1,
    profile_name: '',
    isKid: false,
    iconId: '1',
    order: 'lcn',
}

const profileIdPlaceholder = '-1'

export const useProfilesScreen = (
    profiles: Profile[],
    setProfiles: (profiles: Profile[]) => void,
    lastUsedProfile: Profile | null,
    setLastUsedProfile: (profiles: Profile) => void
) => {
    const [editMenuProfile, setEditMenuProfile] = useState<
        Profile | INewProfile | null
    >(null)
    const navigate = useNavigate()
    const [actualProfile, setActualProfile] = useState<Profile | null>(null)
    const [shouldFetchProfiles, setShouldFetchProfiles] = useState(false)
    const [showWarningMessage, setShowWarningMessage] = useState({
        name: '',
        id: profileIdPlaceholder,
    })
    const { updateUserProfile, deleteUserProfile, fetchUserProfiles } =
        useLoginAndProfileRepository()
    const [shouldRefreshCache, setShouldRefreshCache] = useState(false)
    const [isProfileDeleted, setIsProfileDeleted] = useState(false)

    const { getFavoritesChannels } = useFavoritesChannels()
    const { clearCache } = useEPGCache()
    const { clearEpgStore } = useEPGStore()
    const { clearTvStore } = useTVStore()
    const { setClearVod } = useVODStore()
    const lastProfileNameRef = useRef<string | null>(null)
    const { setActualCategory } = useFilterStore()

    const {
        pin,
        setPin,
        isPinValid,
        setIsPinValid,
        isPinError,
        setIsPinError,
        shouldCheckPin,
        setShouldCheckPin,
        handleCheckPin,
    } = usePinVerification(true)

    const { authenticateAndFetchConfig } = useAuthenticationConfig()

    const {
        createProfile,
        cancelProfile,
        isNewProfileSet,
        newProfile,
        setNewProfile,
    } = useProfileManagement()

    const isCreateProfileDisabled =
        isNewProfileSet || profiles.length >= MAX_PROFILES_LIMIT

    const submitChangeProfile = async (profile: Profile) =>
        await updateUserProfile(profile)

    const clearStoreAndCache = () => {
        clearCache()
        setClearVod()
        clearEpgStore()
        clearTvStore()
    }

    const handleAddProfile = () => {
        if (lastUsedProfile?.isKid) {
            setIsPinValid(false)
            setIsPinError('')
            setShouldCheckPin({
                shouldCheck: true,
                id: profileIdPlaceholder,
                isKid: lastUsedProfile.isKid,
            })
            setNewProfile(blankProfile)
        } else {
            if (newProfile?.order) {
                handleCreateProfile()
            } else {
                setNewProfile(blankProfile)
            }
        }
    }

    const handleCreateProfile = () => {
        createProfile(() => setShouldFetchProfiles(true))
    }

    const handleOkEditProfile = (
        editMenuProfile: Profile | INewProfile | null
    ) => {
        const updatedProfile = editMenuProfile as Profile
        const oldProfile = profiles.find(
            (profile) => profile.id === updatedProfile.id
        )

        if (updatedProfile.id === oldProfile?.id) {
            setShouldRefreshCache(true)
        }

        submitChangeProfile(editMenuProfile as Profile)
        setShouldFetchProfiles(true)
        setEditMenuProfile(null)
        setPin('')
    }

    const handleEditProfileIsKidCheckbox = (
        editMenuProfile: Profile | INewProfile | null
    ) => {
        if (editMenuProfile?.isKid) {
            setShouldCheckPin({
                shouldCheck: true,
                id: profileIdPlaceholder,
                isKid: editMenuProfile.isKid,
            })
            setIsPinValid(false)
        }

        !editMenuProfile?.isKid &&
            setEditMenuProfile(
                (prev) =>
                    prev && {
                        ...prev,
                        isKid: !prev.isKid,
                    }
            )
    }

    const handleWarningScreenOkButton = () => {
        deleteUserProfile(showWarningMessage.id)
        setShowWarningMessage({
            name: '',
            id: profileIdPlaceholder,
        })
        setShouldFetchProfiles(true)
        setIsProfileDeleted(true)
    }

    const handleSelectProfile = async (profile: Profile) => {
        setActualCategory(undefined, true)
        if (lastUsedProfile?.isKid && !profile.isKid) {
            setShouldCheckPin({
                shouldCheck: true,
                id: profile.id,
                isSelect: true,
            })
        }

        if (!lastUsedProfile?.isKid) {
            clearStoreAndCache()
            setLastUsedProfile(profile)
            setActualProfile(profile)
            saveToLocalStorage('lastActualProfile', `${profile?.id}`)
            navigate('/')
        }

        if (lastUsedProfile?.isKid === profile.isKid) {
            clearStoreAndCache()
            setLastUsedProfile(profile)
            setActualProfile(profile)
            saveToLocalStorage('lastActualProfile', `${profile?.id}`)
            navigate('/')
        }

        setIsPinValid(false)
    }

    const handleSetNewProfile = (profile: INewProfile | null) =>
        setNewProfile(
            (prev) =>
                prev && {
                    ...prev,
                    isKid: !profile?.isKid,
                }
        )

    const handleTypeNewProfile = (value: string) =>
        setNewProfile(
            (prev) =>
                prev && {
                    ...prev,
                    profile_name: value,
                }
        )

    const handleSetEditMenu = (value: string) =>
        setEditMenuProfile(
            (prev) =>
                prev && {
                    ...prev,
                    profile_name: value,
                }
        )
    const handleChangePin = (value: string) => {
        setPin(value)
        setIsPinError('')
    }
    const handleCancelPin = () => {
        {
            setEditMenuProfile(null)
            setShouldCheckPin({
                shouldCheck: false,
                id: profileIdPlaceholder,
                isEdit: false,
                isSelect: false,
                isDelete: false,
            })
            setNewProfile(null)
            setPin('')
            setIsPinError('')
        }
    }

    const handleOnEditProfile = (profile: Profile) => {
        if (lastUsedProfile?.isKid && profile.id !== lastUsedProfile.id) {
            setShouldCheckPin({
                shouldCheck: true,
                id: profile.id,
                isKid: profile.isKid,
                isEdit: true,
            })
            setIsPinValid(false)
        } else {
            setEditMenuProfile(profile)
        }
    }

    const handleOnDeleteProfile = (profile: Profile) => {
        if (lastUsedProfile?.isKid) {
            setShouldCheckPin({
                shouldCheck: true,
                id: profile.id,
                isDelete: true,
            })
            setIsPinValid(false)
        } else {
            setShowWarningMessage({
                id: profile.id,
                name: profile.profile_name,
            })
        }
    }
    const handleSetShowWarningMessage = () => {
        setShowWarningMessage({ name: '', id: profileIdPlaceholder })
    }

    useEffect(() => {
        if (!isPinValid) return

        if (editMenuProfile?.profile_name) {
            setEditMenuProfile(
                (prev) => prev && { ...prev, isKid: !editMenuProfile.isKid }
            )
        }

        if (shouldCheckPin.isSelect) {
            const profile = profiles.find(
                (item) => item.id === shouldCheckPin.id
            )
            if (profile) {
                setLastUsedProfile(profile)
                setActualProfile(profile)
                setShouldCheckPin({
                    shouldCheck: false,
                    id: profileIdPlaceholder,
                    isSelect: false,
                })
                navigate('/')
            }
        }

        if (shouldCheckPin.isEdit) {
            const profile = profiles.find(
                (item) => item.id === shouldCheckPin.id
            )
            if (profile) {
                setEditMenuProfile(profile)
                setLastUsedProfile(profile)
                setActualProfile(profile)
                setShouldCheckPin({
                    shouldCheck: false,
                    id: profileIdPlaceholder,
                    isEdit: false,
                })
            }
        }

        if (shouldCheckPin.isDelete) {
            const profile = profiles.find(
                (item) => item.id === shouldCheckPin.id
            )
            if (profile) {
                clearStoreAndCache()
                setShowWarningMessage({
                    name: profile.profile_name,
                    id: profile.id,
                })
                navigate('/')
                setShouldCheckPin({
                    shouldCheck: false,
                    id: profileIdPlaceholder,
                    isDelete: false,
                })
            }
        }
    }, [isPinValid])

    useEffect(() => {
        getFavoritesChannels()
        if (
            actualProfile?.profile_name &&
            actualProfile?.profile_name !== lastProfileNameRef.current
        ) {
            authenticateAndFetchConfig()
            lastProfileNameRef.current = actualProfile?.profile_name
        }

        if (shouldRefreshCache) {
            authenticateAndFetchConfig()
            setShouldRefreshCache(false)
        }
    }, [actualProfile?.profile_name, shouldRefreshCache])

    useEffect(() => {
        const handleUpdateProfile = debounce(async () => {
            try {
                const res = (await fetchUserProfiles()) as Profile[]
                if (res) {
                    setProfiles(res)
                    setShouldFetchProfiles(false)
                }
            } catch (error) {
                getErrorMessage(
                    'Failed to fetch profile in handleUpdateProfile:',
                    error
                )
            }
        }, debounceSpeedMedium)
        if (shouldFetchProfiles) handleUpdateProfile()

        return () => handleUpdateProfile.clear()
    }, [shouldFetchProfiles])

    useEffect(() => {
        if (isProfileDeleted && profiles.length) {
            setLastUsedProfile(profiles[0])
            setActualProfile(profiles[0])
            setIsProfileDeleted(false)
        } else if (isProfileDeleted && !profiles.length) {
            setLastUsedProfile(defaultProfile)
            setActualProfile(defaultProfile)
            setIsProfileDeleted(false)
        }
    }, [profiles.length])

    return {
        handleOnDeleteProfile,
        handleOnEditProfile,
        handleCreateProfile,
        setEditMenuProfile,
        handleTypeNewProfile,
        isCreateProfileDisabled,
        showWarningMessage,
        setShowWarningMessage,
        lastUsedProfile: actualProfile,
        handleSetEditMenu,
        editMenuProfile,
        newProfile,
        handleSetNewProfile,
        handleAddProfile,
        cancelProfile,
        shouldCheckPin,
        handleCancelPin,
        pin,
        setPin,
        isPinError,
        setIsPinError,
        handleOkEditProfile,
        handleEditProfileIsKidCheckbox,
        handleCheckPin,
        handleWarningScreenOkButton,
        handleSelectProfile,
        handleChangePin,
        setNewProfile,
        handleSetShowWarningMessage,
    }
}
